import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, LogOut, ShieldCheck, IdCard, Users, MessageSquare, UserCircle, ShoppingBag } from "lucide-react";
import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { Button } from "@/components/ui/button";
import {
  Sheet, SheetContent, SheetTrigger, SheetTitle, SheetHeader,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { API } from "@/lib/api";
import NotificationBell from "@/components/NotificationBell";
import ThemeToggle from "@/components/ThemeToggle";
import { useAppConfig } from "@/context/AppConfigContext";

const BASE_NAV = [
  { to: "/timeline", label: "Postagens" },
  { to: "/comunidade", label: "Comunidade" },
  { to: "/guia-do-diagnostico", label: "Diagnóstico" },
  { to: "/produtos", label: "Produtos", shopOnly: true },
  { to: "/cursos", label: "Educacional" },
  { to: "/direitos", label: "Direitos" },
  { to: "/apoiar", label: "Apoiar" },
  { to: "/doacoes", label: "Doações" },
  { to: "/contato", label: "Contato" },
];

function initialsOf(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase() || "").join("") || "IC";
}

export default function Header() {
  const { user, logout } = useAuth();
  const { totalQty } = useCart();
  const { shopEnabled } = useAppConfig();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const authed = user && typeof user === "object";
  const isAdmin = authed && user.role === "admin";
  const photoUrl = authed && user.profile_photo_path ? `${API}/files/${user.profile_photo_path}` : null;

  // Loja é ocultada quando shopEnabled = false (padrão para App Store review).
  const NAV = BASE_NAV.filter((n) => !n.shopOnly || shopEnabled);

  const doLogout = async () => {
    await logout();
    navigate("/");
  };

  const renderCartButton = (testid) => (
    <button
      type="button"
      onClick={() => navigate("/carrinho")}
      data-testid={testid}
      aria-label="Abrir carrinho"
      className="relative inline-flex items-center justify-center h-10 w-10 rounded-full border border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card))] hover:bg-[hsl(var(--muted))] transition-colors"
    >
      <ShoppingBag className="h-5 w-5" />
      {totalQty > 0 && (
        <span data-testid="cart-badge" className="absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 rounded-full bg-[hsl(var(--primary))] text-white text-[10px] font-bold flex items-center justify-center border-2 border-white">
          {totalQty > 9 ? "9+" : totalQty}
        </span>
      )}
    </button>
  );

  return (
    <header className="sticky top-0 z-40 border-b border-[hsl(var(--border))] bg-white/85 dark:bg-[hsl(var(--card))]/85 backdrop-blur-xl overflow-x-hidden">
      <div className="container-cuidar flex h-20 items-center justify-between gap-3">
        <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
          <img
            src="/logo-instituto-cuidar.png"
            alt="Instituto Cuidar"
            className="h-12 sm:h-14 lg:h-16 w-auto object-contain"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-4 xl:gap-5 flex-1 justify-center max-w-3xl mx-2">
          {NAV.filter((n) => !n.auth || authed).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-link-${n.label.toLowerCase()}`}
              className={({ isActive }) =>
                `link-underline text-[13px] xl:text-sm font-medium whitespace-nowrap ${isActive ? "text-[hsl(var(--primary))]" : "text-[hsl(var(--foreground))]/80 hover:text-[hsl(var(--foreground))]"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          {shopEnabled && renderCartButton("header-cart-btn")}
          <NotificationBell testid="header-notif-bell" />
          <ThemeToggle testid="header-theme-toggle" />
          {!authed && (
            <>
              <Button variant="ghost" className="btn-pill" onClick={() => navigate("/entrar")} data-testid="header-login-btn">
                Entrar
              </Button>
              <Button
                className="btn-pill bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90"
                onClick={() => navigate("/cadastro")}
                data-testid="header-register-btn"
              >
                Cadastrar
              </Button>
            </>
          )}
          {authed && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="header-user-menu-btn"
                  className="flex items-center gap-2 rounded-full border border-[hsl(var(--border))] bg-white dark:bg-[hsl(var(--card))] px-2 py-1 pr-4 hover:bg-[hsl(var(--muted))] transition-colors"
                >
                  <Avatar className="h-8 w-8">
                    {photoUrl && <AvatarImage src={photoUrl} alt={user.name} />}
                    <AvatarFallback className="bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] font-heading font-bold text-xs">
                      {initialsOf(user.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium">{user.name?.split(" ")[0]}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-2xl">
                <DropdownMenuLabel className="font-heading">{user.name}</DropdownMenuLabel>
                <div className="px-2 pb-1 text-xs text-[hsl(var(--muted-foreground))]">{user.email}</div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate("/perfil")} data-testid="menu-profile">
                  <UserCircle className="mr-2 h-4 w-4" /> Meu perfil
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/associado")} data-testid="menu-associado">
                  <ShieldCheck className="mr-2 h-4 w-4" /> Área do Associado
                </DropdownMenuItem>
                {isAdmin && (
                  <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                    <ShieldCheck className="mr-2 h-4 w-4" /> Painel Admin
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => navigate("/carteirinha")} data-testid="menu-carteirinha">
                  <IdCard className="mr-2 h-4 w-4" /> Carteirinha
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/comunidade")} data-testid="menu-community">
                  <Users className="mr-2 h-4 w-4" /> Comunidade
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate("/timeline")} data-testid="menu-timeline">
                  <MessageSquare className="mr-2 h-4 w-4" /> Postagens
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={doLogout} data-testid="menu-logout" className="text-[hsl(var(--destructive))] focus:text-[hsl(var(--destructive))]">
                  <LogOut className="mr-2 h-4 w-4" /> Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        <div className="lg:hidden flex items-center gap-2">
          {shopEnabled && renderCartButton("mobile-cart-btn")}
          <NotificationBell testid="mobile-notif-bell" />
          <ThemeToggle testid="mobile-theme-toggle" />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn" className="rounded-full">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px]">
              <SheetHeader>
                <SheetTitle className="text-left">
                  <img src="/logo-instituto-cuidar.png" alt="Instituto Cuidar" className="h-10 w-auto object-contain" />
                </SheetTitle>
              </SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                {NAV.filter((n) => !n.auth || authed).map((n) => (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === "/"}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `rounded-xl px-4 py-3 text-sm font-medium ${isActive ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" : "hover:bg-[hsl(var(--muted))]"}`
                    }
                  >
                    {n.label}
                  </NavLink>
                ))}
                <div className="mt-4 border-t border-[hsl(var(--border))] pt-4 flex flex-col gap-2">
                  {!authed && (
                    <>
                      <Button className="btn-pill" variant="outline" onClick={() => { setOpen(false); navigate("/entrar"); }} data-testid="mobile-login-btn">Entrar</Button>
                      <Button className="btn-pill bg-[hsl(var(--primary))] text-white hover:bg-[hsl(var(--primary))]/90" onClick={() => { setOpen(false); navigate("/cadastro"); }} data-testid="mobile-register-btn">Cadastrar</Button>
                    </>
                  )}
                  {authed && (
                    <div className="flex items-center gap-3 rounded-2xl bg-[hsl(var(--muted))] p-3 mb-1">
                      <Avatar className="h-10 w-10">
                        {photoUrl && <AvatarImage src={photoUrl} alt={user.name} />}
                        <AvatarFallback className="bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))] font-heading font-bold text-sm">
                          {initialsOf(user.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="text-sm font-heading font-bold truncate">{user.name}</div>
                        <div className="text-xs text-[hsl(var(--muted-foreground))] truncate">{user.email}</div>
                      </div>
                    </div>
                  )}
                  {authed && (
                    <Button className="btn-pill" variant="outline" onClick={() => { setOpen(false); navigate("/perfil"); }} data-testid="mobile-profile-btn">
                      <UserCircle className="mr-2 h-4 w-4" /> Meu perfil
                    </Button>
                  )}
                  {authed && (
                    <Button className="btn-pill" variant="outline" onClick={() => { setOpen(false); navigate("/associado"); }} data-testid="mobile-associate-btn">
                      <IdCard className="mr-2 h-4 w-4" /> Área do Associado
                    </Button>
                  )}
                  {authed && isAdmin && (
                    <Button className="btn-pill" variant="outline" onClick={() => { setOpen(false); navigate("/admin"); }} data-testid="mobile-admin-btn">
                      <ShieldCheck className="mr-2 h-4 w-4" /> Painel Admin
                    </Button>
                  )}
                  {authed && (
                    <Button className="btn-pill" variant="ghost" onClick={() => { setOpen(false); doLogout(); }} data-testid="mobile-logout-btn">
                      <LogOut className="mr-2 h-4 w-4" /> Sair
                    </Button>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
